/**
 * Branch Statement PDF Template
 *
 * Generates a single-page PDF for one branch showing invoices and payments.
 * Used by multiFilePDFGenerator to create individual branch PDFs.
 *
 * Phase 3C - Consolidated Statement PDF Template (Individual Branch Format)
 * User Story: US-005 (Invoice Generation Enhancement)
 */

import React from 'react'
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer'
import { getFarmLogoUrl } from '../../supabase-client'
import { parseDateString } from '../../utils/dateUtils'

// Define styles (reused from ConsolidatedStatementTemplate but simplified for single branch)
const styles = StyleSheet.create({
  page: {
    fontFamily: 'Times-Roman',
    fontSize: 10,
    paddingTop: 35,
    paddingLeft: 35,
    paddingRight: 35,
    paddingBottom: 65,
    lineHeight: 1.5,
  },

  header: {
    flexDirection: 'row',
    marginBottom: 20,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
  },

  headerLeft: {
    flex: 1,
  },

  headerRight: {
    flex: 1,
    alignItems: 'flex-end',
  },

  logo: {
    maxWidth: 100,
    maxHeight: 80,
    marginBottom: 10,
    objectFit: 'contain',
  },

  companyName: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 5,
  },

  companyInfo: {
    fontSize: 9,
    lineHeight: 1.3,
    marginBottom: 2,
  },

  statementTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'right',
  },

  statementSubtitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 5,
    textAlign: 'right',
  },

  statementDetails: {
    fontSize: 10,
    textAlign: 'right',
    lineHeight: 1.4,
  },

  customerSection: {
    marginBottom: 20,
  },

  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 8,
    textDecoration: 'underline',
  },

  customerText: {
    fontSize: 10,
    lineHeight: 1.4,
    marginBottom: 2,
  },

  periodText: {
    fontSize: 11,
    marginTop: 5,
    fontStyle: 'italic',
  },

  // Summary Section
  summarySection: {
    marginBottom: 20,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#000000',
  },

  summaryTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    backgroundColor: '#f0f0f0',
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
  },

  summaryContent: {
    padding: 12,
  },

  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5,
    fontSize: 10,
  },

  summaryLabel: {
    fontWeight: 'bold',
  },

  summaryValue: {
    textAlign: 'right',
    minWidth: 80,
  },

  summaryHighlight: {
    fontSize: 11,
    marginTop: 6,
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: '#000000',
  },

  // Tables
  invoicesTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 8,
    marginTop: 5,
  },

  invoicesTable: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#cccccc',
    marginBottom: 15,
  },

  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f8f8f8',
    borderBottomWidth: 1,
    borderBottomColor: '#cccccc',
    padding: 5,
  },

  headerText: {
    fontSize: 9,
    fontWeight: 'bold',
  },

  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    padding: 5,
  },

  tableCell: {
    fontSize: 9,
  },

  // Column widths
  invoiceDateCol: { width: '20%' },
  invoiceNoCol: { width: '25%' },
  invoiceAmountCol: { width: '25%', textAlign: 'right' },
  invoiceStatusCol: { width: '30%', textAlign: 'right' },

  paymentDateCol: { width: '25%' },
  paymentMethodCol: { width: '30%' },
  paymentAmountCol: { width: '45%', textAlign: 'right' },

  noDataText: {
    fontSize: 9,
    fontStyle: 'italic',
    color: '#666666',
    padding: 8,
  },

  footer: {
    position: 'absolute',
    bottom: 30,
    left: 35,
    right: 35,
    textAlign: 'center',
    borderTopWidth: 1,
    borderTopColor: '#000000',
    paddingTop: 10,
  },

  footerText: {
    fontSize: 9,
    color: '#666666',
    marginBottom: 3,
  },

  pageNumber: {
    fontSize: 9,
    color: '#666666',
  },
})

const BranchStatementTemplate = ({ statementData, farmInfo }) => {
  const logoUrl = farmInfo?.logo_file_path ? getFarmLogoUrl(farmInfo.logo_file_path) : null

  const formatCurrency = (amount) => `£${parseFloat(amount).toFixed(2)}`

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = parseDateString(dateString)
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const branch = statementData.branch

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {logoUrl && (
              <View style={{ marginBottom: 10 }}>
                <Image style={styles.logo} src={logoUrl} />
              </View>
            )}
            <Text style={styles.companyName}>{farmInfo.business_name}</Text>
            {farmInfo.address_line_1 && (
              <Text style={styles.companyInfo}>{farmInfo.address_line_1}</Text>
            )}
            {farmInfo.address_line_2 && (
              <Text style={styles.companyInfo}>{farmInfo.address_line_2}</Text>
            )}
            {farmInfo.city && (
              <Text style={styles.companyInfo}>
                {farmInfo.city}{farmInfo.postcode && `, ${farmInfo.postcode}`}
              </Text>
            )}
            {farmInfo.phone && (
              <Text style={styles.companyInfo}>Tel: {farmInfo.phone}</Text>
            )}
            {farmInfo.email && (
              <Text style={styles.companyInfo}>Email: {farmInfo.email}</Text>
            )}
          </View>

          <View style={styles.headerRight}>
            <Text style={styles.statementTitle}>BRANCH STATEMENT</Text>
            <Text style={styles.statementSubtitle}>{branch.branchName}</Text>
            {branch.costCenterCode && (
              <Text style={styles.statementDetails}>
                Cost Center: {branch.costCenterCode}
              </Text>
            )}
            <Text style={styles.statementDetails}>
              Generated: {formatDate(statementData.generatedAt)}
            </Text>
          </View>
        </View>

        {/* Customer Information */}
        <View style={styles.customerSection}>
          <Text style={styles.sectionTitle}>Customer Information</Text>
          <Text style={styles.customerText}>
            {statementData.customerInfo.name}
          </Text>
          {statementData.customerInfo.email && (
            <Text style={styles.customerText}>
              {statementData.customerInfo.email}
            </Text>
          )}
          <Text style={styles.customerText}>
            Type: {statementData.customerInfo.type}
          </Text>
          <Text style={styles.periodText}>
            Statement Period: {formatDate(statementData.startDate)} to {formatDate(statementData.endDate)}
          </Text>
        </View>

        {/* Branch Summary */}
        <View style={styles.summarySection}>
          <Text style={styles.summaryTitle}>Branch Summary</Text>
          <View style={styles.summaryContent}>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Total Invoiced:</Text>
              <Text style={styles.summaryValue}>
                {formatCurrency(statementData.totals.totalInvoiced)}
              </Text>
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Total Paid:</Text>
              <Text style={styles.summaryValue}>
                {formatCurrency(statementData.totals.totalPaid)}
              </Text>
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Outstanding Invoices:</Text>
              <Text style={styles.summaryValue}>
                {formatCurrency(statementData.totals.totalOutstanding)}
              </Text>
            </View>
            <View style={[styles.summaryRow, styles.summaryHighlight]}>
              <Text style={styles.summaryLabel}>Current Balance:</Text>
              <Text style={styles.summaryValue}>
                {formatCurrency(statementData.totals.currentBalance)}
              </Text>
            </View>
          </View>
        </View>

        {/* Invoices */}
        <Text style={styles.invoicesTitle}>Invoices</Text>
        {branch.invoices && branch.invoices.length > 0 ? (
          <View style={styles.invoicesTable}>
            <View style={styles.tableHeader}>
              <Text style={[styles.headerText, styles.invoiceDateCol]}>Date</Text>
              <Text style={[styles.headerText, styles.invoiceNoCol]}>Invoice No.</Text>
              <Text style={[styles.headerText, styles.invoiceAmountCol]}>Amount</Text>
              <Text style={[styles.headerText, styles.invoiceStatusCol]}>Status</Text>
            </View>
            {branch.invoices.map((invoice, index) => (
              <View key={invoice.id || index} style={styles.tableRow}>
                <Text style={[styles.tableCell, styles.invoiceDateCol]}>
                  {formatDate(invoice.date)}
                </Text>
                <Text style={[styles.tableCell, styles.invoiceNoCol]}>
                  {invoice.invoiceNo}
                </Text>
                <Text style={[styles.tableCell, styles.invoiceAmountCol]}>
                  {formatCurrency(invoice.total)}
                </Text>
                <Text style={[styles.tableCell, styles.invoiceStatusCol]}>
                  {invoice.paid ? 'Paid' : 'Outstanding'}
                </Text>
              </View>
            ))}
          </View>
        ) : (
          <Text style={styles.noDataText}>No invoices in this period</Text>
        )}

        {/* Payments */}
        <Text style={styles.invoicesTitle}>Payments</Text>
        {branch.payments && branch.payments.length > 0 ? (
          <View style={styles.invoicesTable}>
            <View style={styles.tableHeader}>
              <Text style={[styles.headerText, styles.paymentDateCol]}>Date</Text>
              <Text style={[styles.headerText, styles.paymentMethodCol]}>Method</Text>
              <Text style={[styles.headerText, styles.paymentAmountCol]}>Amount</Text>
            </View>
            {branch.payments.map((payment, index) => (
              <View key={payment.id || index} style={styles.tableRow}>
                <Text style={[styles.tableCell, styles.paymentDateCol]}>
                  {formatDate(payment.date)}
                </Text>
                <Text style={[styles.tableCell, styles.paymentMethodCol]}>
                  {payment.method || 'N/A'}
                </Text>
                <Text style={[styles.tableCell, styles.paymentAmountCol]}>
                  {formatCurrency(payment.amount)}
                </Text>
              </View>
            ))}
          </View>
        ) : (
          <Text style={styles.noDataText}>No payments in this period</Text>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            Branch Statement - Part of Consolidated Account
          </Text>
          <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          } />
        </View>
      </Page>
    </Document>
  )
}

export default BranchStatementTemplate
